import { Box, Link, Typography } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { useFormik } from 'formik';
import { useCallback, FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useGetLegalDocumentsQuery } from '../../../../api/legal-documents';
import { YouCanComeBackNote } from '../../../../general/components/footers/you-can-come-back-note';
import { RequiredText } from '../../../../general/components/required-text';
import { WizardPage } from '../../../../general/components/wizard-page';
import { FormikToggle } from '../../../../general/inputs/formik-toggle';
import { formatCentsCurrency } from '../../../../general/utils/formatters';
import { recordConsent } from '../../../legal-consent/reducers/consent';
import { SubmitWorkflowButton } from '../../../workflow/components/submit-workflow-button';
import { CreditTermsText } from '../../components/credit-terms-text';
import { selectCreditTerms } from '../../reducers/credit-terms.reducer';
import { nextStep, selectnextStep } from '../../reducers/next-step';

const selector = createSelector([selectnextStep, selectCreditTerms], ({ loading }, { creditTerms }) => ({
  finalCreditLimit: creditTerms?.finalCreditLimit,
  billingCycle: creditTerms?.billingCycle,
  gracePeriodDays: creditTerms?.gracePeriodDays,
  saving: loading,
}));

const CounterOfferPage: FC = () => {
  const { finalCreditLimit, billingCycle, gracePeriodDays, saving } = useSelector(selector);

  const { data, isFetching: isFetchingLegalDocs } = useGetLegalDocumentsQuery();
  const commercialFinancingDisclosure = data?.entities.commercial_financing_disclosure;

  const dispatch = useDispatch();
  const handleContinue = useCallback(() => {
    if (commercialFinancingDisclosure) {
      dispatch(
        recordConsent({
          documentKeys: ['commercial_financing_disclosure'],
          onSuccess: [
            nextStep({
              action: 'counter_offer',
              gotoNextPage: true,
            }),
          ],
        }),
      );
    } else {
      dispatch(
        nextStep({
          action: 'counter_offer',
          gotoNextPage: true,
        }),
      );
    }
  }, [commercialFinancingDisclosure, dispatch]);

  const validationSchema = yup.object({
    consenstOfferSummary: yup.mixed().oneOf([true], 'You must read and accept the Coast Offer Summary'),
  });

  const formik = useFormik({
    initialValues: {
      consenstOfferSummary: false,
    },
    validationSchema: commercialFinancingDisclosure ? validationSchema : undefined,
    validateOnChange: false,
    onSubmit: handleContinue,
  });

  return (
    finalCreditLimit &&
    !isFetchingLegalDocs && (
      <WizardPage
        title="Your Updated Credit Terms"
        subtext={
          <>
            Based on the business revenue we were able to verify, you have now been <br />
            prequalified for the following credit limit:
          </>
        }
        footer={<YouCanComeBackNote />}
      >
        <Box my={8.5}>
          <Typography variant="h3-bold" component="p" color="primary.main" align="center">
            {formatCentsCurrency(finalCreditLimit)}
          </Typography>
        </Box>
        <CreditTermsText billingCycle={billingCycle} gracePeriodDays={gracePeriodDays} />
        <form onSubmit={formik.handleSubmit}>
          {commercialFinancingDisclosure && (
            <FormikToggle
              formik={formik}
              id="consenstOfferSummary"
              name="consenstOfferSummary"
              label={
                <RequiredText>
                  I have reviewed and acknowledge receipt of the{' '}
                  <Link href={commercialFinancingDisclosure.url} target="_blank">
                    Coast Offer Summary
                  </Link>
                </RequiredText>
              }
              toggleStyle="checkbox"
            />
          )}
          <SubmitWorkflowButton type="submit" progress={saving} sx={{ mt: 3 }}>
            Accept and Continue
          </SubmitWorkflowButton>
        </form>
      </WizardPage>
    )
  );
};

export { CounterOfferPage };
